import { defineStore } from 'pinia';
import IodineGrouperService, { InitData } from 'src/services/IodineGrouperService';
import { GrouperService } from 'src/services/GrouperService';
import { computed, ref } from 'vue';
import { GrouperData } from 'src/contracts/grouperData';
import { addDiagnosisCodes } from 'src/common/codeUtil';
import { Subscription } from 'rxjs';
import { Services } from 'src/services/service-manager';
import { DischargeStatus } from 'src/contracts/discharge-status';
import { isEmpty } from 'src/common/arrayUtil';
import { useLayoutStore } from 'stores/layout-store';
import { CalculateDrgRequestDto } from 'src/contracts/calculateDrgRequestDto';
import { DrgResponseDto } from 'src/contracts/drgResponseDto';
import { DiagnosisCode } from 'src/contracts/diagnosisCode';
import { ProcedureCode } from 'src/contracts/procedureCode';
import { PatientData } from 'src/contracts/patientData';
import {GrouperInfo} from 'src/contracts/grouperInfo';
import { EditData } from 'src/contracts/edit';
import {useResourcesStore} from 'stores/resources-store';
import { queue } from 'async';
import { getDiagnosesFromCalculationResult } from 'src/common/diagnosis-helper';

interface CalculationArgs {
  calculate_primary: boolean,
  calculate_secondary: boolean
}

export const useGrouperStore = defineStore('grouperstore', () => {

  const calculateQueue = queue((args: CalculationArgs, callback) => {
    calculateDrg(args).then(() => callback()).catch(callback);
  }, 1);

  const diagnosisCodes = ref<DiagnosisCode[]>([]);
  const resequence = ref<boolean>(false);

  const grouperInfo= ref<GrouperInfo>()
  const dualGrouping = ref<boolean>(false);
  const getCodesCount = computed(() => diagnosisCodes.value.length + procedureCodes.value.length);
  const procedureCodes = ref<ProcedureCode[]>([]);
  const edits = ref<EditData[]>([]);
  const isPrimaryDrgMs = computed(() => grouperInfo?.value?.grouperSource?.grouper_type == 'CMS');
  const primaryDrg = ref<DrgResponseDto>({});
  const secondaryDrg = ref<DrgResponseDto>({});
  const patient = ref<PatientData | undefined>({
    name: 'Default Patient',
    dischargeDate: '00/00/0000',
    sex: 'N',
    codingSystem: 'AAA'
  });
  const dischargeStatuses = ref<DischargeStatus[]>([]);
  const getDischargeStatuses = computed(() => dischargeStatuses.value);
  const dischargeStatus = ref<DischargeStatus | undefined>();
  const aprCalculationAvailable = computed(() => diagnosisCodes.value.length > 0 && aprRecalculationNeeded.value);
  const calculatingApr = ref(false);
  const isCalculatingApr = computed(() => calculatingApr.value);
  const aprRecalculationNeeded = ref(false);

  function resetDRGData() {
    diagnosisCodes.value = [];
    procedureCodes.value = [];
  }

  async function recalculateNeeded() {

    aprRecalculationNeeded.value = true;
    // automatically recalc MS
    calculateQueue.push({
      calculate_primary: !!isPrimaryDrgMs?.value,
      calculate_secondary: !isPrimaryDrgMs?.value
    });
  }

  // temporary helper function
  function searchCodes(text: string | undefined): string {

    switch(text) {
      case 'R65.21':
        return 'Sever sepsis with septic shock should be MCC and cause the DRG to have MCC';
      case 'A00.9':
        return 'Cholera unspecified';
      case 'C00.1':
        return 'Maligant neoplasm of exernal lower lip';
      case '86932':
        return 'Frozen blood freeze/thaw';
      case '01246':
        return 'Repair Cerebral Meninges, Open Approach';
      case '03410':
        return 'Bypass 4+ Cor Art from Thor Art w Zooplastic, Open';
      case 'T81.530A':
        return 'Some Hac code';
      case 'E87.3':
      case 'J43.9':
      case 'E08.10':
      case 'J18.9':
        return `Diagnosis Code ${text}`;
    }

    return '';
  }

  async function addDiagnosisCode(code: string | undefined, description: string | undefined, poa: string | undefined): Promise<void> {
    diagnosisCodes.value.push({
      pdx: false,
      drgModifier: [],
      present_on_admission: poa || undefined,
      quality: [],
      risk: [],
      id: diagnosisCodes.value.length,
      action: '',
      code,
      description
    });
    await recalculateNeeded();
  }

  async function changeResequence() {
    resequence.value = !resequence.value
    await recalculateNeeded()
  }

  async function addProcedureCode(code: string | undefined, description: string | undefined): Promise<void> {
    procedureCodes.value?.push({
      action: '',
      code,
      id: procedureCodes.value?.length || 0,
      impact: [],
      slot: [],
      description
    });
    await recalculateNeeded()
  }

  async function calculateDrg(calcArgs: CalculationArgs): Promise<void> {
    const grouperData: CalculateDrgRequestDto = {
      diagnoses: diagnosisCodes.value,
      procedures: procedureCodes.value,
      calculate_primary: calcArgs.calculate_primary,
      calculate_secondary: calcArgs.calculate_secondary,
      resequence: resequence.value
    };

    const grouperService = Services.service(GrouperService);
    const drgResponseDtos = await grouperService.calculateDrg(grouperData);
    console.log(`DRG Calculate Response: ${JSON.stringify(drgResponseDtos)}`)
    handleDrgEdits(drgResponseDtos);
    handleDrgData(drgResponseDtos, calcArgs.calculate_primary, calcArgs.calculate_secondary);
    diagnosisCodes.value = getDiagnosesFromCalculationResult(drgResponseDtos);

    await useResourcesStore().getMsDrgAnalysis();

    if (calcArgs.calculate_primary && calcArgs.calculate_secondary) {
      aprRecalculationNeeded.value = false;
    }
  }

  function calculateApr(): void {

    aprRecalculationNeeded.value = false;
    calculatingApr.value = true;
    calculateQueue.push({
      calculate_primary: !isPrimaryDrgMs?.value,
      calculate_secondary: !!isPrimaryDrgMs?.value
    }, (err) => {
      calculatingApr.value = false;
      if (err) {
        console.error(err);
        aprRecalculationNeeded.value = true;
      }
    });
  }

  async function saveDrg(): Promise<void> {
    const grouperData: any  = {
      diagnoses: convertDiagnosis(),
      procedures: procedureCodes.value,
      grouper_state: grouperInfo.value?.grouperState
    };

    console.log(JSON.stringify(grouperData));

    const grouperService = Services.service(GrouperService);
    const drgResponseDto = await grouperService.saveDrg(grouperData);
    console.log(`DRG save Response: ${JSON.stringify(drgResponseDto)}`)
  }

  function convertDiagnosis(): any[] {
    return diagnosisCodes.value.map(diagnose => {
      return {
        id: diagnose.id,
        present_on_admission: diagnose.present_on_admission,
        code: diagnose.code,
      }
    })
  }

  let setVisitContextSubscription: Subscription | undefined;
  let addCodeSubscription: Subscription | undefined;

  function createAddCodeSubscription() {
    addCodeSubscription = IodineGrouperService.addCodes$.subscribe(
      async (addCodeData: GrouperData) => {
        console.log(`Adding code(s): ${JSON.stringify(addCodeData)}`);

        addDiagnosisCodes(diagnosisCodes.value, addCodeData.diagnosisCodes);
        if (!isEmpty(addCodeData?.procedureCodes)) {
          addCodeData.procedureCodes?.forEach(procedure => {
            addProcedureCode(procedure.code, procedure.description);
          });
        }
        await recalculateNeeded();
      }
    );
  }

  function createContextSubscription() {
    setVisitContextSubscription = IodineGrouperService.setVisitContext$.subscribe(
      async (initData: InitData) => {
        resetDRGData();
        useLayoutStore().changeMode(initData.mode, false);
        Services.setDefaultConfig(initData.endpoint, initData.visitId, initData.facilityCode);
        dualGrouping.value = initData.dualGrouping;

        const grouperService = Services.service(GrouperService);
        const response = await grouperService.getGrouperData();

        if (isEmpty(dischargeStatuses.value)) {
          dischargeStatuses.value = await grouperService.getDischargeStatuses();
        }

        handleDrgData(response.drgInfo, true, true);
        grouperInfo.value = response.grouperInfo;
        diagnosisCodes.value = response.diagnosisCodes || [];
        procedureCodes.value = response.procedureCodes || [];
        patient.value = response.patient;

        useResourcesStore().getMsDrgAnalysis();
      }
    );
  }

  function handleDrgEdits(drgResponseDto: DrgResponseDto[] | undefined): void {
    // reset edits
    edits.value = [];

    drgResponseDto?.forEach(drg => {
      drg.edits?.edit.forEach(edit => {
        edits.value.push(edit);

        const index = parseInt(edit.position || '-1');
        switch (edit.content_type) {
          case 'diagnoses':
            const diagnosisCode = diagnosisCodes.value?.at(index);
            if (diagnosisCode) {
              diagnosisCode.edit = edit;
            }
            break;
          case 'procedures':
            const procedureCode = procedureCodes.value?.at(index);
            if (procedureCode) {
              procedureCode.edit = edit;
            }
            break;
          default:
            console.log(`Unexpected content type: ${edit.contentType}`);
            break;
            // throw new Error(`Unexpected content type: ${edit.contentType}`);
        }
      })
    })
  }

  function handleDrgData(drgResponseDto: DrgResponseDto[], primaryCalculated: boolean, secondaryCalculated: boolean): void {
    if (isEmpty(drgResponseDto)) {
      primaryDrg.value = {};
      secondaryDrg.value = {};
      return;
    }

    if (primaryCalculated) {
      primaryDrg.value = drgResponseDto.find(drg => drg?.priority == 'PRIMARY') || {};
    }

    if (secondaryCalculated) {
      secondaryDrg.value = drgResponseDto.find(drg => drg?.priority != 'PRIMARY') || {};
    }
  }

  function setAllPoaYes() {
    if (diagnosisCodes?.value?.some(dx => dx.present_on_admission != 'Y')) {
      diagnosisCodes?.value?.forEach(dxCode => {
        dxCode.present_on_admission = 'Y';
      });
      recalculateNeeded();
    }
  }

  function $onMount() {
    createContextSubscription();
    createAddCodeSubscription();
  }

  function $reset() {
    setVisitContextSubscription && setVisitContextSubscription.unsubscribe();
    addCodeSubscription && addCodeSubscription.unsubscribe();
  }

  return {
    diagnosisCodes,
    getCodesCount,
    procedureCodes,
    edits,
    resequence,
    primaryDRG: primaryDrg,
    secondaryDRG: secondaryDrg,
    patient,
    aprCalculationAvailable,
    recentCodeUpdate: aprRecalculationNeeded,
    searchCodes,
    addDiagnosisCode,
    addProcedureCode,
    $reset,
    $onMount,
    setVisitContextSubscription,
    addCodeSubscription,
    calculateApr,
    saveDrg,
    changeResequence,
    getDischargeStatuses,
    dischargeStatus,
    isPrimaryDrgMs,
    recalculateNeeded,
    isCalculatingApr,
    setAllPoaYes
  }
})
